/**
 * Why people are leaving, on the screen where the leaving is measured.
 *
 * Until now this was a link: the adoption screen said "os motivos moram na tela de resultados" and
 * sent you there. The link survives — but the two questions ("quantos saíram?" and "por quê?") are
 * asked in the same breath, and answering one screen away meant nobody read the answer.
 *
 * **This does not break R23.** That rule is about module dependencies inside the BFF — surveys
 * reads opt-in, never the reverse — and it stays exactly as it was: `GET /adoption` and
 * `GET /surveys/:id/results` are two independent calls, and the composition happens here, in the
 * screen. The BFF already does the same thing with `exitSurveyId`, resolved on the surveys side and
 * stitched into the bootstrap. Nothing was added to the adoption payload.
 *
 * **What is embedded, and what is not.** The distribution per question — the answer the old
 * "Motivos de saída" panel used to give — and the free text, which today is collected and read by
 * nobody (the same fate `reasonText` had). The funnel, the respondents table, the CSV and the
 * version picker stay on the results screen: duplicating those would create two screens to keep
 * identical, and the first divergence is the end of both.
 *
 * **This block used to carry a caveat, and it is gone because the thing it warned about is gone.**
 * It said "a unidade aqui é a resposta, não a pessoa", because the questionnaire was asked at every
 * exit: whoever left twice answered twice, so the cards above (people) and the numbers here
 * (responses) did not have to agree. Since R33 it is asked **once per person per version**,
 * guaranteed by `@@unique([surveyId, surveyVersion, platformUserId])` — one row is one person, and
 * there is nothing left to reconcile. The whole screen counts people now, this block included, and
 * it says so on the number itself instead of warning about a collision that cannot happen (R22b).
 *
 * **The cut by one answer lives here too, and that is the whole reason it exists** (R49). The case
 * that produced it is this questionnaire: reading "qual item ficou mais difícil de encontrar"
 * **dentre quem marcou 'não encontrei um item'" no motivo. Leaving the cut on the results screen
 * only would have put the feature one screen away from the question that asked for it — the same
 * distance this block was built to close. It comes with every rule it has there: only the charts
 * and the free text narrow, each cut number states its base, an empty cut is a sentence and not an
 * error, and a sticky band says what did **not** move. What did not move here is the adoption
 * chart and its cards above, which count people who mostly never answered anything.
 *
 * *What this block still does not get:* the funnel, the respondents table, the CSV and the version
 * picker. The cut is inside the delivered version, like everything else here.
 *
 * *The neighbour that did NOT change:* the adoption chart above still carries two units on purpose
 * — people and toggle events —, because one person switching on, off and on again in a day is one
 * person and three events. That distinction is information, not ambiguity, and it is not to be
 * "aligned" with this.
 *
 * @module Pages/BUs/OptInFeatures
 */

import { css } from '@emotion/css';
import { GLIcon } from '@group-link-one/gl-icons';
import { UTButton, UTLoading } from '@group-link-one/gl-ui/utilities';
import { useMemo, useState } from 'react';
import type { JSX } from 'react';
import { formatCount } from '../../../Utils/adoptionFormat';
import { exitSurveyInFocus, splitExitQuestions } from '../../../Utils/exitSurveyPanel';
/*
  Imported from the Surveys module rather than copied, and rather than moved to `Shared/components`.

  These two renderers are typed on `SurveyQuestionResult` / `SurveyComment` and lean on
  `surveyFormat` for labels and scales, so moving them to `Shared` would drag a module's domain
  types into a folder that today knows about none — a worse dependency than the one below. Reading
  the surveys module from here is already the established direction on these screens: `useExitSurveys`
  wraps `useSurveys`, and the ④ block of the feature form reads `surveyFormat`. Direction preserved,
  one renderer, no second drawing of the same chart.
*/
import { QuestionResultCard } from '@/Pages/BUs/Surveys/Components/SurveyResults/Components/QuestionResultCard';
import { ResultsComments } from '@/Pages/BUs/Surveys/Components/SurveyResults/Components/ResultsComments';
import { ResultsCutControl } from '@/Pages/BUs/Surveys/Components/SurveyResults/Components/ResultsCutControl';
import { ResultsCutNotice } from '@/Pages/BUs/Surveys/Components/SurveyResults/Components/ResultsCutNotice';
import { useSurveyComments } from '@/Pages/BUs/Surveys/Hooks/useSurveyComments';
import { useSurveyResults } from '@/Pages/BUs/Surveys/Hooks/useSurveyResults';
import type { Survey, SurveyResultsCut } from '@/Pages/BUs/Surveys/Types/Surveys.types';
import {
  CUT_FULL_BASE_NOTICE_ADOPTION,
  CUT_FULL_BASE_TAG,
  cutEmptyDescription,
  cutEmptyTitle,
  cuttableQuestions,
  isEmptyCut,
} from '@/Pages/BUs/Surveys/Utils/resultsCut';
import {
  EXIT_STATE_CONSEQUENCES,
  EXIT_STATE_LABELS,
  EXIT_STATE_TONES,
  exitSurveyState,
  surveyTitle,
} from '@/Pages/BUs/Surveys/Utils/surveyFormat';
import { EmptyState } from '@/Shared/components/EmptyState/EmptyState';
import { StatusTag } from '@/Shared/components/StatusTag/StatusTag';

const panelStyle = css`
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-card);
  padding: var(--space-ml);
  display: flex;
  flex-direction: column;
  gap: var(--space-m);
  min-width: 0;
`;

const headStyle = css`
  display: flex;
  align-items: center;
  gap: var(--space-s);
  flex-wrap: wrap;
`;

const titleStyle = css`
  margin: 0;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
`;

const surveyTitleStyle = css`
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
`;

const slugStyle = css`
  font-family: var(--font-mono);
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
`;

const spacerStyle = css`
  flex: 1;
`;

const hintStyle = css`
  margin: 0;
  font-size: var(--font-size-xs);
  color: var(--color-text-muted);
  text-wrap: pretty;
`;

const warningStyle = css`
  display: flex;
  align-items: flex-start;
  gap: var(--space-s);
  padding: var(--space-m) var(--space-ml);
  border: 1px solid var(--color-warning);
  border-radius: var(--radius-m);
  background: var(--color-warning-subtle);
  font-size: var(--font-size-xs);
  text-wrap: pretty;
`;

const errorStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
  padding: var(--space-m) var(--space-ml);
  border: 1px solid var(--color-border-strong);
  border-left: 3px solid var(--color-danger);
  border-radius: var(--radius-m);
  background: var(--color-surface-hover);
  font-size: var(--font-size-xs);
  text-wrap: pretty;
`;

/**
 * Two columns, not the three of the results screen.
 *
 * This block shares the page with the adoption cards and the people table, so it gets less room
 * than a screen whose only subject is the questionnaire. Wide types keep their own span there;
 * here everything is one column wide, because a grid question inside an embedded panel would push
 * the table below it off the first screen.
 */
const questionsGridStyle = css`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(300px, 100%), 1fr));
  gap: var(--space-base);
  align-items: stretch;
`;

const cardCellStyle = css`
  display: flex;
  min-width: 0;

  > * {
    flex: 1;
  }
`;

const textBlockStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-s);
  padding-top: var(--space-s);
  border-top: 1px solid var(--color-divider);
`;

const textTitleStyle = css`
  margin: 0;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
`;

/**
 * Five, not the twenty-five of the results screen.
 *
 * This list sits between the charts and the people table, and a page of twenty-five pushes the
 * table off the screen — the block would have taken over the page it is embedded in. Five is
 * enough to see that people wrote real sentences, and the pager says how many more there are.
 */
const FREE_TEXT_PAGE_SIZE = 5;

export interface OptInExitSurveyResultsProps {
  /** Every `ON_OPT_OUT` survey naming this feature, in whatever state — from `useExitSurveys`. */
  surveys: Survey[];
  /** The one a person leaving actually gets, or `null` when none of them is servable (R35). */
  served: Survey | null;
  isLoading: boolean;
  /** Whether the results screen is reachable at all — same flag its route is behind. */
  canReadResults: boolean;
  onOpenResults: (surveyId: string) => void;
}

export const OptInExitSurveyResults = ({
  surveys,
  served,
  isLoading,
  canReadResults,
  onOpenResults,
}: OptInExitSurveyResultsProps): JSX.Element | null => {
  const [commentsPage, setCommentsPage] = useState(1);
  const [questionKey, setQuestionKey] = useState('');
  /** The pair, or nothing: one param without the other is a `400` that names no group (R49). */
  const [cut, setCut] = useState<SurveyResultsCut | undefined>();

  const target = exitSurveyInFocus(served, surveys);

  /*
    The questionnaire in focus is read — the served one, or, when nothing is being asked, the
    newest: a questionnaire that fell out of approval still holds everything answered while it was
    approved, and hiding that would turn a warning into a blank panel. Only the permission stops
    the request, because without it the screen behind "Ver resultados completos" is closed too.

    The version is deliberately left out: what shows up is the delivered one, the same default the
    results screen opens on.
  */
  const surveyId = canReadResults ? target?.id : undefined;

  const {
    data: results,
    isLoading: isLoadingResults,
    error,
  } = useSurveyResults(surveyId, undefined, Boolean(surveyId), cut);

  const { data: commentsPageData, isLoading: isLoadingComments } = useSurveyComments(surveyId, {
    page: commentsPage,
    limit: FREE_TEXT_PAGE_SIZE,
    ...(questionKey ? { questionKey } : {}),
    ...cut,
  });

  /*
    What the cut may be made on, in the version this block reads. Same helper as the results
    screen, so the same four types are refused before a click, not after a `400`.
  */
  const cutQuestions = useMemo(() => cuttableQuestions(results?.questions), [results]);

  /*
    No questionnaire attached: no panel. This is the behaviour from before the results were
    embedded, and it is right — an empty box promising reasons reads as something broken, and the
    answer "esta funcionalidade não pergunta nada a quem sai" is already given by the ④ block of
    the feature form, which is where it can be acted on.
  */
  if (isLoading || !target) return null;

  const state = exitSurveyState(target);
  const { charted: chartedQuestions, textFilterOptions } = splitExitQuestions(results?.questions);

  return (
    <section id='opt-in-adoption-exit-survey' className={panelStyle}>
      <div className={headStyle}>
        <h2 className={titleStyle}>Por que as pessoas saem</h2>
        <span className={surveyTitleStyle}>{surveyTitle(target)}</span>
        <span className={slugStyle}>{target.slug}</span>
        <StatusTag
          id='opt-in-adoption-exit-survey-state'
          status={EXIT_STATE_TONES[state]}
          size='sm'
          dot
        >
          {EXIT_STATE_LABELS[state]}
        </StatusTag>
        <div className={spacerStyle} />
        <ResultsCutControl
          id='opt-in-adoption-exit-survey-cut'
          questions={cutQuestions}
          cut={cut}
          onChange={next => {
            setCut(next);
            setCommentsPage(1);
          }}
        />
        {canReadResults ? (
          <UTButton
            fitContent
            variant='secondary'
            size='small'
            id='opt-in-adoption-exit-survey-results'
            text='Ver resultados completos'
            icon='Misc.ChartLineUp'
            onClick={() => {
              onOpenResults(target.id);
            }}
          />
        ) : null}
      </div>

      {/*
        R35, in the same words the ④ block of the feature form uses: a questionnaire that is not
        served asks nobody anything and raises no error anywhere. Here it also explains why there
        are no numbers below — without it, "nenhuma resposta" would look like indifference rather
        than like a questionnaire that was never delivered.
      */}
      {!served ? (
        <div id='opt-in-adoption-exit-survey-warning' className={warningStyle} role='alert'>
          <GLIcon variant='Notifications.WarningCircle' size={16} fill='var(--gl-yellow-dark)' />
          <span>
            <strong>Ninguém está sendo perguntado.</strong> {EXIT_STATE_CONSEQUENCES[state]}{' '}
            Enquanto estiver assim, o que houver aqui embaixo é o que já tinha sido respondido antes
            — nada novo entra.
          </span>
        </div>
      ) : null}

      {!canReadResults ? (
        <p className={hintStyle}>
          As respostas ficam na tela de resultados da pesquisa, e seu acesso não inclui essa tela.
        </p>
      ) : null}

      {/*
        The unit on the number itself, not as a caveat underneath it (R22b). One response per person
        per version, so "quantas respostas" and "quantas pessoas" are the same count, and the line
        that used to reconcile the two has nothing left to reconcile.

        The version is named for the same reason the empty state names it: versions are never pooled
        (R2), so this is a count of one of them.
      */}
      {results && results.totalSubmitted > 0 ? (
        <p className={hintStyle}>
          <strong>{formatCount(results.totalSubmitted)} pessoas</strong> responderam a versão{' '}
          {results.version}. Cada pessoa responde uma vez por versão, então todo número aqui embaixo
          conta gente.
          {/*
            This sentence is about everybody who answered, cut or no cut — it is the denominator the
            cut is measured against, not one of the numbers the cut moves.
          */}
          {results.filter ? ` ${CUT_FULL_BASE_TAG}.` : ''}
        </p>
      ) : null}

      {results?.filter ? (
        <ResultsCutNotice
          id='opt-in-adoption-exit-survey-cut-notice'
          filter={results.filter}
          /* No funnel and no rate here — the neighbours are the adoption cards, so it names those. */
          fullBaseNotice={CUT_FULL_BASE_NOTICE_ADOPTION}
          onClear={() => {
            setCut(undefined);
            setCommentsPage(1);
          }}
        />
      ) : null}

      {isLoadingResults ? <UTLoading type='dots' /> : null}

      {/*
        Only a real failure, never "there is no data yet": the two calls are independent (R23), so
        this one falling over leaves the adoption numbers above standing, and the panel says so
        instead of putting an error banner over a screen that is otherwise fine.
      */}
      {error ? (
        <div id='opt-in-adoption-exit-survey-error' className={errorStyle} role='alert'>
          <span>
            Não foi possível carregar as respostas do questionário de saída. Os números de adoção
            acima continuam valendo — são outra chamada.
          </span>
          <span>{error.message}</span>
        </div>
      ) : null}

      {/*
        Nothing answered yet gets a sentence, never an empty chart: a bar chart of zeros is read as
        "as pessoas responderam e não escolheram nada", which is a different — and false — finding.

        **It says which version, and that is not pedantry.** This block reads the delivered version,
        like the results screen's default, and versions are never pooled (R2) — the questionnaire was
        cloned precisely because the answers of v1 were written against the words of v1. Caught on
        the real `mdm-menu-saida`: one answer on v1, `bump-version` to v2, and a bare "ninguém
        respondeu ainda" beside a card reading "1 resposta" is a contradiction the screen invented.
      */}
      {results && results.totalSubmitted === 0 ? (
        <EmptyState
          id='opt-in-adoption-exit-survey-empty'
          icon='Communication.ChatCircleText'
          title={`Ninguém respondeu a versão ${results.version}`}
          description='Assim que alguém desativar a funcionalidade e responder, a distribuição de cada pergunta e o que foi escrito aparecem aqui. Respostas de versões anteriores não entram nesta conta — foram escritas contra outras palavras, e ficam na tela de resultados, que tem o seletor de versão.'
        />
      ) : null}

      {/*
        A cut that caught nobody: a sentence, never a grid of zeroed bars, and never an error. The
        free-text list goes with it — there is nobody in the group, so there is nothing they wrote.
      */}
      {results?.filter && isEmptyCut(results.filter) ? (
        <EmptyState
          id='opt-in-adoption-exit-survey-cut-empty'
          icon='Users.UserFocus'
          title={cutEmptyTitle}
          description={cutEmptyDescription(results.filter)}
        />
      ) : null}

      {results && results.totalSubmitted > 0 && !isEmptyCut(results.filter) ? (
        <>
          {chartedQuestions.length > 0 ? (
            <div id='opt-in-adoption-exit-survey-questions' className={questionsGridStyle}>
              {chartedQuestions.map(question => (
                <div key={question.key} className={cardCellStyle}>
                  <QuestionResultCard
                    question={question}
                    cut={results.filter}
                    onReadComments={key => {
                      setQuestionKey(key);
                      setCommentsPage(1);
                    }}
                  />
                </div>
              ))}
            </div>
          ) : null}

          {/*
            The free text, listed. It was the whole content of `reasonText` before this model, and
            it went nowhere; the aggregates never carry it — `/results` reports how many people
            wrote something, never what — so a list is the only way it is ever read.
          */}
          <div id='opt-in-adoption-exit-survey-text' className={textBlockStyle}>
            <h3 className={textTitleStyle}>O que escreveram</h3>
            <ResultsComments
              comments={commentsPageData?.comments ?? []}
              total={commentsPageData?.total ?? 0}
              page={commentsPage}
              pageSize={FREE_TEXT_PAGE_SIZE}
              isLoading={isLoadingComments}
              questionKey={questionKey}
              questionOptions={textFilterOptions}
              cut={commentsPageData?.filter}
              onQuestionKeyChange={value => {
                setQuestionKey(value);
                setCommentsPage(1);
              }}
              onPageChange={setCommentsPage}
            />
          </div>
        </>
      ) : null}
    </section>
  );
};
